import request from '@/utils/request'
import axios from 'axios'
import { installAppVersionGuard } from '@/utils/appVersion'
import { ElMessage } from 'element-plus'
import { API_BASE_URL, TOKEN_KEY, USER_INFO_KEY, PERMISSIONS_KEY } from '@/config/constants'
import { resetLoginRedirect } from '@/utils/authRedirect'
import { installAuthGuard } from '@/utils/authSession'

/**
 * 用户登录
 * @param {Object} data - 登录数据
 * @param {string} data.username - 用户名
 * @param {string} data.password - 密码
 * @returns {Promise}
 */
export function login(data) {
  return request({
    url: '/api/auth/login',
    method: 'post',
    data
  })
}

/**
 * 用户登出
 */
export function logout() {
  // 清除所有本地存储的用户相关数据（token、用户信息、工作空间选择、统计缓存等）
  try {
    localStorage.clear()
  } catch (e) {
    // 回退方案，至少移除关键字段
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_INFO_KEY)
  }
}

/**
 * 获取存储的 token
 * @returns {string|null}
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * 设置 token
 * @param {string} token
 */
export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
    resetLoginRedirect()
    console.log('Token 已保存到 localStorage')
  } else {
    console.warn('Token 为空，未保存')
  }
}

/**
 * 获取用户信息
 * @returns {Object|null}
 */
export function getUserInfo() {
  const userInfo = localStorage.getItem(USER_INFO_KEY)
  return userInfo ? JSON.parse(userInfo) : null
}

/**
 * 设置用户信息
 * @param {Object} userInfo
 */
export function setUserInfo(userInfo) {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo))
}

/**
 * 获取用户权限
 * @returns {Array|null}
 */
export function getPermissions() {
  const permissions = localStorage.getItem(PERMISSIONS_KEY)
  return permissions ? JSON.parse(permissions) : null
}

/**
 * 判断当前用户是否拥有指定权限
 * @param {string|string[]} requiredPermissions
 * @returns {boolean}
 */
export function hasPermission(requiredPermissions) {
  if (!requiredPermissions) return true

  const permissions = getPermissions()
  if (!Array.isArray(permissions)) return false
  if (permissions.includes('*:*:*')) return true

  if (Array.isArray(requiredPermissions)) {
    return requiredPermissions.some(permission => permissions.includes(permission))
  }

  return permissions.includes(requiredPermissions)
}

/**
 * 设置用户权限
 * @param {Array} permissions
 */
export function setPermissions(permissions) {
  if (permissions && Array.isArray(permissions)) {
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions))
    console.log('用户权限已保存到 localStorage')
  } else {
    console.warn('权限数据为空或不是数组，未保存')
  }
}

/**
 * 更新用户头像
 * @param {File} file - 头像图片文件
 * @returns {Promise}
 */
export function updateAvatar(file) {
  const formData = new FormData()
  formData.append('avatarFile', file)
  
  // 创建独立的 axios 实例用于文件上传
  const uploadRequest = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000 // 文件上传可能需要更长时间
  })

  // 添加请求拦截器，添加 token
  installAppVersionGuard(uploadRequest)
  installAuthGuard(uploadRequest)
  uploadRequest.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        config.headers.Authorization = token
        config.__authToken = token.startsWith('Bearer ') ? token.substring(7) : token
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // 添加响应拦截器
  uploadRequest.interceptors.response.use(
    (response) => {
      const res = response.data
      if (res.code === 200) {
        return res
      } else {
        ElMessage.error(res.message || '头像更新失败')
        return Promise.reject(new Error(res.message || '头像更新失败'))
      }
    },
    (error) => {
      if (error?.isVersionMismatch || error?.isAuthExpired) return Promise.reject(error)
      console.error('头像更新错误:', error)
      if (error.response) {
        const { status, data } = error.response
        ElMessage.error(data?.message || `头像更新失败: ${status}`)
      } else {
        ElMessage.error('网络错误，请检查网络连接')
      }
      return Promise.reject(error)
    }
  )

  return uploadRequest({
    url: '/api/auth/avatar',
    method: 'post',
    data: formData
  })
}

/**
 * 修改密码
 * @param {Object} data - 密码数据
 * @param {string} data.oldPassword - 旧密码
 * @param {string} data.newPassword - 新密码
 * @returns {Promise}
 */
export function updatePassword(data) {
  return request({
    url: '/api/auth/password',
    method: 'post',
    data: {
      oldPassword: data.oldPassword,
      newPassword: data.newPassword
    }
  })
}

/**
 * 获取生成次数
 * @returns {Promise}
 */
export function getGenerateCount() {
  return request({
    url: '/api/auth/generate-count',
    method: 'get'
  })
}

/**
 * 获取用户信息
 * @param {number|string} userId - 用户ID
 * @returns {Promise}
 */
export function getUserInfoFromApi(userId) {
  return request({
    url: `/api/user/${userId}`,
    method: 'get'
  })
}
